import { showToast } from "@certn/cascadia";
import { DropdownMenu, GhostIconButton, MenuItem } from "@certn/cascadia-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { BulkMonitoringActionType } from "../types";
import { useBulkMonitoringAction } from "../useMonitoringApi";
import { DeactivateMonitoringConfirmModal } from "./DeactivateMonitoringConfirmModal";

interface Props {
  subjectId: string;
  firstName: string | null;
  monitorStatusRollup: string;
  onActionComplete: () => void;
}

export function MonitoringApplicantActionsDropdown({
  subjectId,
  firstName,
  monitorStatusRollup,
  onActionComplete,
}: Props) {
  const { t } = useTranslation();
  const [pendingDeactivateSubjectId, setPendingDeactivateSubjectId] = useState<
    string | null
  >(null);
  const isDeactivateConfirmOpen = pendingDeactivateSubjectId !== null;

  const { submit, isSubmitting } = useBulkMonitoringAction({
    onSuccess: () => {
      showToast(t("Monitoring updated successfully"), { type: "success" });
      setPendingDeactivateSubjectId(null);
      onActionComplete();
    },
  });

  useEffect(() => {
    if (pendingDeactivateSubjectId === null) {
      return;
    }
    if (
      pendingDeactivateSubjectId !== subjectId ||
      monitorStatusRollup !== "ACTIVE"
    ) {
      setPendingDeactivateSubjectId(null);
    }
  }, [pendingDeactivateSubjectId, subjectId, monitorStatusRollup]);

  if (monitorStatusRollup !== "ACTIVE") {
    return null;
  }

  const deactivateLabel = firstName
    ? t("Deactivate {{name}}", { name: firstName })
    : t("Deactivate");

  async function handleAction(action: BulkMonitoringActionType) {
    if (action === "DEACTIVATE") {
      setPendingDeactivateSubjectId(subjectId);
      return;
    }
    await submit({ subject_ids: [subjectId], action });
  }

  async function handleDeactivateConfirm() {
    if (!pendingDeactivateSubjectId) {
      return;
    }
    await submit({
      subject_ids: [pendingDeactivateSubjectId],
      action: "DEACTIVATE",
    });
  }

  return (
    <>
      <DropdownMenu
        placement="bottom end"
        triggerButton={
          <GhostIconButton
            testID="monitoring-applicant-actions-button"
            size="small"
            icon="more_vert"
            aria-label={t("Applicant monitoring actions")}
            isDisabled={isSubmitting}
          />
        }
        onItemAction={(key) =>
          void handleAction(key as BulkMonitoringActionType)
        }
      >
        <MenuItem
          id="PAUSE"
          testID="monitoring-applicant-action-pause"
          icon="pause_circle"
        >
          {t("Pause all monitoring")}
        </MenuItem>
        <MenuItem
          id="DEACTIVATE"
          testID="monitoring-applicant-action-deactivate"
          icon="do_not_disturb_on"
          iconColor="icon-danger"
          textColor="text-danger"
        >
          {deactivateLabel}
        </MenuItem>
      </DropdownMenu>
      <DeactivateMonitoringConfirmModal
        isOpen={isDeactivateConfirmOpen}
        onClose={() => setPendingDeactivateSubjectId(null)}
        onConfirm={() => void handleDeactivateConfirm()}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
